import React, {
  FocusEventHandler,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import classNames from 'classnames';
import { ArrowAngleDown } from '@zooplus/icons';
import { Input, InputProps } from '@zooplus/ui-components';
import { isMobile } from 'react-device-detect';
import { KeycloakContext } from '../../context/KeycloakContext';
import { scrollElementToTop } from '../../../utils/DomUtils';
import { useBodyEvents } from '../../../hooks';
import useFocusBlurEvents from './hooks/useFocusBlurEvents';
import { OrderSelectorOptionSelector } from './OrderSelectorOptionSelector';
import './order-selector.scss';
import { OrderCompositeField } from '../../../hooks/validation/types';

type OrderSelectorFieldProps = InputProps & { setValue: (value: OrderCompositeField) => void };

export const OrderSelector = (props: OrderSelectorFieldProps) => {
  const keycloakContext = useContext(KeycloakContext);
  const isUserLoggedIn = !!keycloakContext.tokenData?.customerId;
  const orderSelectorRef = useRef<HTMLDivElement>(null);
  const orderSelectorInputRef = useRef<HTMLInputElement>(null);
  const { setValue, onFocus, inputProps, ...otherInputProps } = props;
  const { onBlur, onKeyPress, ...inputPropsInner } = inputProps!;
  const [showSelectionArrow, setShowSelectionArrow] = useState(true);

  const {
    onFocusWrapped,
    onBlurWrapped,
    onInteractionStart,
    closeOrderSelectorAndFocus,
    abandonOrderSelector,
    toggleOrderSelectorAndFocus,
    isSelectorOpen,
  } = useFocusBlurEvents(
    orderSelectorRef,
    onFocus as FocusEventHandler<HTMLElement | undefined>,
    onBlur as FocusEventHandler<HTMLElement | undefined>,
  );

  const selectOrderId = useCallback(
    (orderId: string, suborderId: string) => {
      setValue({ orderId, suborderId });
      closeOrderSelectorAndFocus();
    },
    [setValue, closeOrderSelectorAndFocus],
  );

  useEffect(() => {
    if (isSelectorOpen && isMobile) {
      scrollElementToTop(orderSelectorRef.current);
    }
  }, [isSelectorOpen]);

  const { startCapturing, stopCapturing } = useBodyEvents(
    orderSelectorRef,
    undefined,
    abandonOrderSelector,
  );
  useEffect(() => {
    if (isSelectorOpen) {
      startCapturing();
    } else {
      stopCapturing();
    }
  }, [startCapturing, stopCapturing, isSelectorOpen]);

  return (
    <div
      className={'orderSelector'}
      ref={orderSelectorRef}
      onMouseDown={onInteractionStart}
      onTouchStart={onInteractionStart}>
      <Input
        {...otherInputProps}
        inputProps={{
          ...inputPropsInner,
          autoComplete: isUserLoggedIn ? 'off' : 'on',
          onBlur: onBlurWrapped,
        }}
        onFocus={onFocusWrapped}
        ref={orderSelectorInputRef}
        className={classNames({ orderSelector__input: isUserLoggedIn })}
      />
      {isUserLoggedIn && !props.isDisabled && showSelectionArrow && (
        <div className={'orderSelector__arrow'} onClick={toggleOrderSelectorAndFocus}>
          <ArrowAngleDown data-test="orderSelector.arrow" />
        </div>
      )}
      {isUserLoggedIn && (
        <OrderSelectorOptionSelector
          isOpen={isSelectorOpen}
          selectOrderId={selectOrderId}
          selectedSuborder={inputProps?.value as string}
          onError={() => setShowSelectionArrow(false)}
          filterValue={inputProps!.value as string}
        />
      )}
    </div>
  );
};
